/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
    ChevronLeft,
    RotateCcw,
    Trophy,
    Award,
    Info,
    Layers,
    ChevronRight,
    Flame,
    Search,
    BookOpen,
    Calendar,
    Save,
    RefreshCw
} from 'lucide-react';
import { TEAMS } from '../constants';
import { collection, deleteDoc } from 'firebase/firestore';
import { db, doc, setDoc, onSnapshot, handleFirestoreError, OperationType, isFirestoreQuotaError, notifyQuotaExceeded } from '../firebase';
import { LanguageCode, TRANSLATIONS } from '../translations';

interface GroupsPageProps {
    user?: any;
    onSelectTeam: (teamId: string) => void;
    lang: string;
}

export interface ScoreEntry {
    scoreA: number | "";
    scoreB: number | "";
}

export interface ScoreMap {
    [matchKey: string]: ScoreEntry;
}

export interface TeamStandings {
    teamId: string;
    name: string;
    flag: string;
    c1: string;
    c2: string;
    played: number;
    won: number;
    drawn: number;
    lost: number;
    gf: number;
    ga: number;
    gd: number;
    pts: number;
}

export const GROUPS_DATA = [
    { id: "A", name: "Group A", teams: ["MEX", "RSA", "KOR", "CZE"] },
    { id: "B", name: "Group B", teams: ["CAN", "BIH", "QAT", "SUI"] },
    { id: "C", name: "Group C", teams: ["BRA", "MAR", "HAI", "SCO"] },
    { id: "D", name: "Group D", teams: ["USA", "PAR", "AUS", "TUR"] },
    { id: "E", name: "Group E", teams: ["GER", "CUW", "CIV", "ECU"] },
    { id: "F", name: "Group F", teams: ["NED", "JPN", "SWE", "TUN"] },
    { id: "G", name: "Group G", teams: ["BEL", "EGY", "IRN", "NZL"] },
    { id: "H", name: "Group H", teams: ["ESP", "CPV", "KSA", "URU"] },
    { id: "I", name: "Group I", teams: ["FRA", "SEN", "IRQ", "NOR"] },
    { id: "J", name: "Group J", teams: ["ARG", "ALG", "AUT", "JOR"] },
    { id: "K", name: "Group K", teams: ["POR", "COD", "UZB", "COL"] },
    { id: "L", name: "Group L", teams: ["ENG", "CRO", "GHA", "PAN"] },
];

export const GroupsPage = ({ user, onSelectTeam, lang }: GroupsPageProps) => {
    const t = (key: string) => {
        const activeLang = lang || 'en';
        return TRANSLATIONS[activeLang as LanguageCode]?.[key] || TRANSLATIONS['en']?.[key] || key;
    };

    const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Super user authorization check
    const isSuperUser = user && user.email === 'gonzos978@gmail.com';

    const [isSyncingAPI, setIsSyncingAPI] = useState(false);
    const [syncMessage, setSyncMessage] = useState<string | null>(null);

    const handleFullAPISync = async () => {
        setIsSyncingAPI(true);
        setSyncMessage(null);
        try {
            const response = await fetch("/api/football/sync-all-finished-worldcup");
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || "Failed to trigger API-Football synchronization");
            }
            if (data.success) {
                setSyncMessage(`Synced ${data.syncedMatchesCount} matches!`);
                alert(`🔌 API-Football Standings & Results Synchronized!\n\nSuccessfully matched and synchronized ${data.syncedMatchesCount} completed World Cup stage matches from API-Football.\n\nAll official scores, corners, game statistics, standings, table stats, user points, and rewards have been calculated and populated in Firebase!`);
            } else {
                throw new Error(data.message || "Failed synchronization");
            }
        } catch (error: any) {
            console.error(error);
            alert(`API-Football Sync failed: ${error.message}`);
        } finally {
            setIsSyncingAPI(false);
        }
    };

    // Scores Map (synced with Firestore)
    const [scores, setScores] = useState<ScoreMap>({});

    // Real-time Firestore Sync
    useEffect(() => {
        const scoresCol = collection(db, 'groups_scores');

        const unsubscribe = onSnapshot(scoresCol, (snapshot) => {
            const dbScores: ScoreMap = {};
            snapshot.forEach((docSnap) => {
                const data = docSnap.data();
                if (data && data.matchKey) {
                    dbScores[data.matchKey] = {
                        scoreA: data.scoreA !== undefined ? data.scoreA : "",
                        scoreB: data.scoreB !== undefined ? data.scoreB : "",
                    };
                }
            });
            setScores(dbScores);
        }, (error) => {
            if (isFirestoreQuotaError(error)) {
                notifyQuotaExceeded();
            } else {
                handleFirestoreError(error, OperationType.LIST, 'groups_scores');
            }
        });

        return () => unsubscribe();
    }, []);

    // Setup Standard Match list for round-robin
    const getMatchesForGroup = (groupId: string, teamsList: string[]) => {
        const [t1, t2, t3, t4] = teamsList;
        return [
            { id: `${groupId}_1`, home: t1, away: t2, round: 1 },
            { id: `${groupId}_2`, home: t3, away: t4, round: 1 },
            { id: `${groupId}_3`, home: t1, away: t3, round: 2 },
            { id: `${groupId}_4`, home: t2, away: t4, round: 2 },
            { id: `${groupId}_5`, home: t1, away: t4, round: 3 },
            { id: `${groupId}_6`, home: t2, away: t3, round: 3 },
        ];
    };

    // Draft scores for active group to make editing lightning-fast, lag-free, and saveable via button
    const [draftScores, setDraftScores] = useState<ScoreMap>({});
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

    // Load database scores into draft state when starting to edit a group
    useEffect(() => {
        if (activeGroupId) {
            const activeGroup = GROUPS_DATA.find(g => g.id === activeGroupId);
            if (activeGroup) {
                const matches = getMatchesForGroup(activeGroupId, activeGroup.teams);
                const initialDrafts: ScoreMap = {};
                matches.forEach(m => {
                    const matchKey = `${activeGroupId}_${m.home}_vs_${m.away}`;
                    initialDrafts[matchKey] = scores[matchKey] ? { ...scores[matchKey] } : { scoreA: "", scoreB: "" };
                });
                setDraftScores(initialDrafts);
            }
        } else {
            setDraftScores({});
        }
    }, [activeGroupId, scores]);

    // Compute if active group has unsaved draft changes
    const hasChanges = useMemo(() => {
        if (!activeGroupId) return false;
        const activeGroup = GROUPS_DATA.find(g => g.id === activeGroupId);
        if (!activeGroup) return false;
        const matches = getMatchesForGroup(activeGroupId, activeGroup.teams);
        return matches.some(m => {
            const matchKey = `${activeGroupId}_${m.home}_vs_${m.away}`;
            const draftMatch = draftScores[matchKey] || { scoreA: "", scoreB: "" };
            const currentMatch = scores[matchKey] || { scoreA: "", scoreB: "" };
            return draftMatch.scoreA !== currentMatch.scoreA || draftMatch.scoreB !== currentMatch.scoreB;
        });
    }, [activeGroupId, draftScores, scores]);

    // Helper to delete document in Firestore
    const clearMatchInFirestore = async (matchKey: string) => {
        if (!isSuperUser) return;
        try {
            await deleteDoc(doc(db, 'groups_scores', matchKey));
        } catch (err) {
            handleFirestoreError(err, OperationType.DELETE, `groups_scores/${matchKey}`);
        }
    };

    // Mass save active group changes to database
    const saveChangesToDatabase = async () => {
        if (!isSuperUser || !activeGroupId) return;
        const activeGroup = GROUPS_DATA.find(g => g.id === activeGroupId);
        if (!activeGroup) return;

        setIsSaving(true);
        setSaveStatus('idle');

        try {
            const matches = getMatchesForGroup(activeGroupId, activeGroup.teams);
            for (const m of matches) {
                const matchKey = `${activeGroupId}_${m.home}_vs_${m.away}`;
                const draftMatch = draftScores[matchKey];

                if (!draftMatch || (draftMatch.scoreA === "" && draftMatch.scoreB === "")) {
                    await clearMatchInFirestore(matchKey);
                } else {
                    const firstScore = draftMatch.scoreA === "" ? 0 : Number(draftMatch.scoreA);
                    const secondScore = draftMatch.scoreB === "" ? 0 : Number(draftMatch.scoreB);

                    await setDoc(doc(db, 'groups_scores', matchKey), {
                        matchKey,
                        groupId: activeGroupId,
                        scoreA: firstScore,
                        scoreB: secondScore,
                        updatedBy: user?.email || 'gonzos978@gmail.com',
                        updatedAt: new Date().toISOString()
                    });
                }
            }
            setSaveStatus('success');
            setTimeout(() => setSaveStatus('idle'), 3000);
        } catch (err) {
            console.error("Error saving draft modifications: ", err);
            setSaveStatus('error');
            handleFirestoreError(err, OperationType.WRITE, `groups_scores_${activeGroupId}`);
        } finally {
            setIsSaving(false);
        }
    };

    // Local handlers for draft scores
    const handleScoreChange = (matchKey: string, scoreType: 'scoreA' | 'scoreB', val: string) => {
        if (!isSuperUser) return;
        const parsed = val === "" ? "" : Math.max(0, parseInt(val, 10) || 0);

        setDraftScores(prev => ({
            ...prev,
            [matchKey]: {
                ...(prev[matchKey] || { scoreA: "", scoreB: "" }),
                [scoreType]: parsed
            }
        }));
    };

    const incrementScore = (matchKey: string, scoreType: 'scoreA' | 'scoreB') => {
        if (!isSuperUser) return;
        setDraftScores(prev => {
            const currentEntry = prev[matchKey] || { scoreA: "", scoreB: "" };
            const current = currentEntry[scoreType] === "" ? 0 : Number(currentEntry[scoreType]);
            return {
                ...prev,
                [matchKey]: {
                    ...currentEntry,
                    [scoreType]: current + 1
                }
            };
        });
    };

    const decrementScore = (matchKey: string, scoreType: 'scoreA' | 'scoreB') => {
        if (!isSuperUser) return;
        setDraftScores(prev => {
            const currentEntry = prev[matchKey] || { scoreA: "", scoreB: "" };
            const current = currentEntry[scoreType] === "" ? 0 : Number(currentEntry[scoreType]);
            if (current <= 0) return prev;
            return {
                ...prev,
                [matchKey]: {
                    ...currentEntry,
                    [scoreType]: current - 1
                }
            };
        });
    };

    const clearMatch = (matchKey: string) => {
        if (!isSuperUser) return;
        setDraftScores(prev => ({
            ...prev,
            [matchKey]: { scoreA: "", scoreB: "" }
        }));
    };

    const resetGroup = async (groupId: string, teamsList: string[]) => {
        if (!isSuperUser) return;
        if (confirm(`Are you sure you want to clear all scores in Group ${groupId}?`)) {
            const gMatches = getMatchesForGroup(groupId, teamsList);
            try {
                const promises = gMatches
                    .map(m => `${groupId}_${m.home}_vs_${m.away}`)
                    .filter(matchKey => scores[matchKey] !== undefined)
                    .map(matchKey => clearMatchInFirestore(matchKey));

                await Promise.all(promises);
                setDraftScores(prev => {
                    const next = { ...prev };
                    gMatches.forEach(m => {
                        const matchKey = `${groupId}_${m.home}_vs_${m.away}`;
                        next[matchKey] = { scoreA: "", scoreB: "" };
                    });
                    return next;
                });
                alert(`Successfully cleared all scores in Group ${groupId}!`);
            } catch (err) {
                console.error("Error resetting group scores: ", err);
                alert(`Failed to clear group scores. Error: ${err instanceof Error ? err.message : String(err)}`);
            }
        }
    };

    const resetAllScores = async () => {
        if (!isSuperUser) return;
        if (confirm("Are you sure you want to completely reset ALL scores in the database? This cannot be undone.")) {
            try {
                const promises = Object.keys(scores).map(matchKey => clearMatchInFirestore(matchKey));
                await Promise.all(promises);
                setDraftScores({});
                alert("Successfully reset all standings and scores in the database!");
            } catch (err) {
                console.error("Error resetting all scores: ", err);
                alert(`Failed to reset standings. Error: ${err instanceof Error ? err.message : String(err)}`);
            }
        }
    };
    // Compute stats for a group
    const calculateStandings = (groupId: string, teamsList: string[], customScores?: ScoreMap): TeamStandings[] => {
        // List default structure
        const stats: { [teamId: string]: TeamStandings } = {};
        const sourceScores = customScores || scores;

        teamsList.forEach(tId => {
            const teamObj = TEAMS.find(x => x.id === tId) || { name: tId, flag: "", c1: "#1e293b", c2: "#475569" };
            stats[tId] = {
                teamId: tId,
                name: teamObj.name,
                flag: teamObj.flag || "",
                c1: teamObj.c1 || "#1e293b",
                c2: teamObj.c2 || "#475569",
                played: 0,
                won: 0,
                drawn: 0,
                lost: 0,
                gf: 0,
                ga: 0,
                gd: 0,
                pts: 0,
            };
        });

        const gMatches = getMatchesForGroup(groupId, teamsList);

        gMatches.forEach(m => {
            const matchKey = `${groupId}_${m.home}_vs_${m.away}`;
            const entry = sourceScores[matchKey];

            if (entry && entry.scoreA !== "" && entry.scoreB !== "") {
                const sA = entry.scoreA as number;
                const sB = entry.scoreB as number;

                // Update Played
                stats[m.home].played++;
                stats[m.away].played++;

                // Goals For and Against
                stats[m.home].gf += sA;
                stats[m.home].ga += sB;
                stats[m.away].gf += sB;
                stats[m.away].ga += sA;

                // Wins/Losses/Draws Points
                if (sA > sB) {
                    stats[m.home].won++;
                    stats[m.home].pts += 3;
                    stats[m.away].lost++;
                } else if (sB > sA) {
                    stats[m.away].won++;
                    stats[m.away].pts += 3;
                    stats[m.home].lost++;
                } else {
                    stats[m.home].drawn++;
                    stats[m.home].pts += 1;
                    stats[m.away].drawn++;
                    stats[m.away].pts += 1;
                }
            }
        });

        // Calc GD for all
        Object.keys(stats).forEach(tId => {
            stats[tId].gd = stats[tId].gf - stats[tId].ga;
        });

        // Sort Standings:
        // 1st: Points
        // 2nd: Goal Difference
        // 3rd: Goals For
        // 4th: Name
        return Object.values(stats).sort((a, b) => {
            if (b.pts !== a.pts) return b.pts - a.pts;
            if (b.gd !== a.gd) return b.gd - a.gd;
            if (b.gf !== a.gf) return b.gf - a.gf;
            return a.name.localeCompare(b.name);
        });
    };

    // Statistics summaries across entire tournament groups
    const computedAllGroups = GROUPS_DATA.map(g => calculateStandings(g.id, g.teams));
    const totalMatchesPlayed = (Object.values(scores) as ScoreEntry[]).filter(s => s.scoreA !== "" && s.scoreB !== "").length;
    const totalGoals = (Object.values(scores) as ScoreEntry[]).reduce((acc: number, current: ScoreEntry) => {
        if (current.scoreA !== "" && current.scoreB !== "") {
            return acc + (current.scoreA as number) + (current.scoreB as number);
        }
        return acc;
    }, 0);

    // Filter groups if query is used
    const filteredGroups = GROUPS_DATA.filter(g => {
        if (!searchQuery) return true;
        const queryLower = searchQuery.toLowerCase();

        // Check if group ID or name aligns
        if (g.name.toLowerCase().includes(queryLower)) return true;

        // Check if a team name in the group aligns
        return g.teams.some(teamId => {
            const matchObj = TEAMS.find(t => t.id === teamId);
            return matchObj?.name.toLowerCase().includes(queryLower);
        });
    });

    const activeGroup = GROUPS_DATA.find(g => g.id === activeGroupId);
    const activeGroupStandings = activeGroupId && activeGroup ? calculateStandings(activeGroupId, activeGroup.teams, isSuperUser ? draftScores : scores) : [];
    const activeGroupMatches = activeGroupId && activeGroup ? getMatchesForGroup(activeGroupId, activeGroup.teams) : [];

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 text-slate-300">
            {/* Super User / Read-Only Authority Banner */}
            {isSuperUser && (
                <div className="mb-6 bg-slate-900 border border-indigo-500/20 rounded-xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <span className="w-2.5 h-2.5 bg-indigo-500 rounded-full animate-pulse flex-shrink-0" />
                        <div>
                            <h4 className="text-sm font-bold text-slate-100 uppercase tracking-wide">Super User Administration</h4>
                            <p className="text-xs text-slate-400 mt-0.5">Signed in as <strong className="text-indigo-400">{user?.email}</strong>. Press sync to populate results, stand list, tables, points, and packs in Firebase via official API-Football.</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
                        {syncMessage && (
                            <span className="text-[11px] font-mono font-bold text-emerald-400 border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 rounded">
                ✓ {syncMessage}
              </span>
                        )}
                        <button
                            onClick={handleFullAPISync}
                            disabled={isSyncingAPI}
                            className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-900 border border-indigo-400/20 disabled:text-indigo-400 text-white rounded-lg text-xs font-black transition-all cursor-pointer flex items-center gap-2 shadow-sm uppercase tracking-wider"
                            id="api-football-sync-btn"
                        >
                            {isSyncingAPI ? (
                                <>
                                    <RefreshCw size={13} className="animate-spin text-indigo-300" />
                                    Syncing live...
                                </>
                            ) : (
                                <>
                                    <RefreshCw size={13} className="text-indigo-300" />
                                    Sync Finished Games (API)
                                </>
                            )}
                        </button>
                        <div className="text-[10px] bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded border border-amber-500/30 uppercase tracking-widest font-black">
                            ADMIN
                        </div>
                    </div>
                </div>
            )}

            <AnimatePresence mode="wait">
                {!activeGroupId ? (
                    // GRID STATE OVERVIEW
                    <motion.div
                        key="overview"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        className="flex flex-col gap-6"
                    >
                        {/* Header Area */}
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-[#1e293b]/40 p-5 rounded-xl border border-slate-800">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-blue-600/10 rounded-lg text-blue-400 border border-blue-500/20">
                                    <Layers size={22} className="text-blue-500" />
                                </div>
                                <div>
                                    <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">{t('wcStageStandings')}</h1>
                                    <p className="text-xs text-slate-400 mt-0.5">{t('editScoresDesc')}</p>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-2.5">
                                {/* Stats */}
                                <span className="text-xs bg-[#0f172a] border border-slate-800 rounded px-2.5 py-1.5 flex items-center gap-1.5 font-mono text-slate-400">
                  <Flame size={13} className="text-orange-400" />
                                    {t('gamesLabel')}: <span className="text-white font-bold">{totalMatchesPlayed}/72</span>
                </span>

                                <span className="text-xs bg-[#0f172a] border border-slate-800 rounded px-2.5 py-1.5 flex items-center gap-1.5 font-mono text-slate-400">
                  <Trophy size={13} className="text-yellow-400" />
                                    {t('goalsSummaryLabel')}: <span className="text-white font-bold">{totalGoals}</span>
                </span>

                                {totalMatchesPlayed > 0 && isSuperUser && (
                                    <button
                                        onClick={resetAllScores}
                                        className="text-xs bg-red-600/15 text-red-400 border border-red-500/30 font-medium px-3 py-1.5 rounded hover:bg-red-500 hover:text-white transition-all flex items-center gap-1 cursor-pointer"
                                    >
                                        <RotateCcw size={13} />
                                        {t('resetStandings')}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Searching Filters */}
                        <div className="relative w-full max-w-md">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none text-slate-500">
                <Search size={15} />
              </span>
                            <input
                                type="text"
                                placeholder={t('searchGroupCountry')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 bg-[#0f172a] border border-slate-800 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                                id="group-search"
                            />
                        </div>

                        {/* Groups Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredGroups.map(group => {
                                const standings = calculateStandings(group.id, group.teams);
                                const playedInG = Object.keys(scores).filter(key => key.startsWith(`${group.id}_`)).length;

                                return (
                                    <motion.div
                                        key={group.id}
                                        whileHover={{ scale: 1.015, y: -2 }}
                                        onClick={() => setActiveGroupId(group.id)}
                                        className="bg-[#131d31] rounded-xl border border-slate-800 hover:border-slate-700 p-4.5 cursor-pointer flex flex-col justify-between transition-all group shadow-md"
                                    >
                                        <div>
                                            {/* Top label */}
                                            <div className="flex items-center justify-between mb-3.5 pb-2.5 border-b border-slate-800/80">
                                                <div className="flex items-center gap-2">
                          <span className="w-6.5 h-6.5 bg-blue-600/20 text-blue-400 font-mono text-sm font-black rounded flex items-center justify-center">
                            {group.id}
                          </span>
                                                    <span className="text-sm font-bold text-white group-hover:text-blue-400 transition-colors font-sans">{group.name}</span>
                                                </div>

                                                <span className="text-[10px] font-mono font-bold text-slate-500 bg-[#0f172a] px-2 py-0.5 rounded border border-slate-800">
                          {playedInG === 6 ? (
                              <span className="text-emerald-400 uppercase">Completed</span>
                          ) : (
                              <span>{playedInG}/6 fixtures</span>
                          )}
                        </span>
                                            </div>

                                            {/* Standings list preview */}
                                            <div className="flex flex-col gap-1.5">
                                                {standings.map((st, index) => {
                                                    const isQualifying = index < 2;
                                                    return (
                                                        <div
                                                            key={st.teamId}
                                                            className="flex items-center justify-between text-xs py-1 px-2 rounded hover:bg-slate-800/20 transition-colors"
                                                        >
                                                            <div className="flex items-center gap-2.5 min-w-0">
                                <span className={`w-4 text-center font-mono font-bold ${isQualifying ? 'text-emerald-400' : 'text-slate-500'}`}>
                                  {index + 1}
                                </span>
                                                                {st.flag && (
                                                                    <img
                                                                        src={`https://flagcdn.com/w20/${st.flag}.png`}
                                                                        alt=""
                                                                        className="w-4 h-3 object-cover rounded-[1px] shadow-sm flex-shrink-0"
                                                                        referrerPolicy="no-referrer"
                                                                        crossOrigin="anonymous"
                                                                    />
                                                                )}
                                                                <span className="text-slate-250 truncate font-semibold font-sans">{st.name}</span>
                                                            </div>

                                                            <div className="flex items-center gap-3.5 font-mono text-[11px] text-slate-400 flex-shrink-0">
                                                                <span>P:{st.played}</span>
                                                                <span className="w-12 text-right">GD:{st.gd >= 0 ? `+${st.gd}` : st.gd}</span>
                                                                <span className="w-8 text-right font-bold text-white bg-[#0f172a]/60 px-1.5 py-0.5 rounded border border-slate-800">
                                  {st.pts} pts
                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div className="mt-4 pt-3 border-t border-slate-800/60 flex items-center justify-between text-[11px] text-blue-400 font-medium">
                                            <span>{t('fixturesTitle')}</span>
                                            <ChevronRight size={13} className="transform group-hover:translate-x-0.5 transition-transform" />
                                        </div>
                                    </motion.div>
                                );
                            })}

                            {filteredGroups.length === 0 && (
                                <div className="col-span-full py-16 text-center text-slate-500">
                                    <div className="mb-2 text-2xl font-mono">⚠️</div>
                                    <p className="text-sm">{t('noActiveTrades')}</p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                ) : (
                    // DETAILED FOCUS SCORE EDITING STATE
                    <motion.div
                        key="focusedGroup"
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        className="flex flex-col gap-6"
                    >
                        {/* Nav Back Header */}
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <button
                                onClick={() => setActiveGroupId(null)}
                                className="flex items-center gap-1.5 text-slate-400 hover:text-white text-sm font-medium transition-colors bg-[#131d31] border border-slate-800 hover:border-slate-700 px-3 py-1.5 rounded-lg cursor-pointer"
                            >
                                <ChevronLeft size={16} />
                                {t('backToOverview')}
                            </button>

                            {isSuperUser && (
                                <div className="flex items-center gap-2.5">
                                    <button
                                        onClick={() => resetGroup(activeGroupId, activeGroup!.teams)}
                                        className="text-xs bg-[#131d31] text-red-400 border border-slate-800 hover:border-slate-700 font-medium px-3 py-1.5 rounded-lg hover:bg-slate-800 transition-all flex items-center gap-1 cursor-pointer"
                                    >
                                        <RotateCcw size={13} />
                                        Reset {activeGroup?.name} Scores
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Core Info Row */}
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-600/10 rounded-lg text-blue-400 border border-blue-500/20 flex items-center justify-center font-bold text-lg font-mono">
                                {activeGroupId}
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-white tracking-tight">{activeGroup?.name} {t('wcStageStandings')}</h2>
                                <p className="text-xs text-slate-400 mt-0.5">{t('editScoresDesc')}</p>
                            </div>
                        </div>

                        {/* Split layout: Standings Table on left, Fixtures on right */}
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

                            {/* Standings Table Card: 7 Columns wide */}
                            <div className="lg:col-span-7 bg-[#131d31] border border-slate-800 rounded-xl p-5 shadow-lg">
                                <h3 className="text-sm font-bold font-mono tracking-widest text-slate-400 uppercase mb-4 flex items-center gap-2">
                                    <Award size={15} className="text-blue-500" /> {t('wcStageStandings')}
                                </h3>

                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-sm whitespace-nowrap">
                                        <thead>
                                        <tr className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-wider border-b border-slate-800 pb-2">
                                            <th className="py-2 pl-2">#</th>
                                            <th className="py-2">{t('team')}</th>
                                            <th className="py-2 text-center w-8">{t('playedTitle')}</th>
                                            <th className="py-2 text-center w-8">{t('wonTitle')}</th>
                                            <th className="py-2 text-center w-8">{t('drawnTitle')}</th>
                                            <th className="py-2 text-center w-8">{t('lostTitle')}</th>
                                            <th className="py-2 text-center w-10">{t('goalsDiffTitle')}</th>
                                            <th className="py-2 text-center w-10">{t('goalsForTitle')}:{t('goalsAgainstTitle')}</th>
                                            <th className="py-2 text-center pr-2 w-12">{t('pointsTitle')}</th>
                                        </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-800/60">
                                        {activeGroupStandings.map((st, i) => {
                                            const isQualifying = i < 2;
                                            return (
                                                <tr
                                                    key={st.teamId}
                                                    className={`group border-b border-slate-800/40 hover:bg-[#1e293b]/20 transition-all ${
                                                        isQualifying ? 'bg-emerald-500/[0.01]' : ''
                                                    }`}
                                                >
                                                    {/* Position */}
                                                    <td className="py-3 pl-2.5 font-mono">
                              <span className={`w-5 h-5 flex items-center justify-center rounded text-xs font-black ${
                                  i === 0
                                      ? 'bg-amber-500/15 text-amber-400 border border-amber-500/20'
                                      : i === 1
                                          ? 'bg-slate-300/15 text-slate-300 border border-slate-300/20'
                                          : 'text-slate-500'
                              }`}>
                                {i + 1}
                              </span>
                                                    </td>

                                                    {/* Team */}
                                                    <td className="py-3">
                                                        <div className="flex items-center gap-2.5">
                                                            {st.flag && (
                                                                <img
                                                                    src={`https://flagcdn.com/w20/${st.flag}.png`}
                                                                    alt=""
                                                                    className="w-5.5 h-4 object-cover rounded-[1px] shadow border border-slate-800 flex-shrink-0"
                                                                    referrerPolicy="no-referrer"
                                                                    crossOrigin="anonymous"
                                                                />
                                                            )}
                                                            <div className="flex flex-col">
                                                                <span className="text-sm font-semibold text-slate-100 font-sans">{st.name}</span>
                                                                <div className="flex items-center gap-2 mt-0.5">
                                                                    <span className="text-[9px] font-mono text-slate-500 font-semibold">{st.teamId}</span>
                                                                    <button
                                                                        onClick={() => onSelectTeam(st.teamId)}
                                                                        className="text-[9px] text-blue-500 hover:text-blue-400 font-mono flex items-center gap-0.5 underline hover:no-underline transition-all"
                                                                        title="Open Sticker Album page for this team"
                                                                    >
                                                                        <BookOpen size={9} />
                                                                        View Stickers
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>

                                                    {/* Stats */}
                                                    <td className="py-3 text-center font-mono font-medium text-slate-200">{st.played}</td>
                                                    <td className="py-3 text-center font-mono text-slate-400">{st.won}</td>
                                                    <td className="py-3 text-center font-mono text-slate-400">{st.drawn}</td>
                                                    <td className="py-3 text-center font-mono text-slate-400">{st.lost}</td>

                                                    {/* Goal Difference */}
                                                    <td className={`py-3 text-center font-mono font-semibold ${
                                                        st.gd > 0 ? 'text-emerald-400' : st.gd < 0 ? 'text-red-400' : 'text-slate-500'
                                                    }`}>
                                                        {st.gd > 0 ? `+${st.gd}` : st.gd}
                                                    </td>

                                                    {/* GD breakdown GF:GA */}
                                                    <td className="py-3 text-center font-mono text-slate-400 text-xs">
                                                        {st.gf}:{st.ga}
                                                    </td>

                                                    {/* PTS */}
                                                    <td className="py-3 text-center pr-2">
                              <span className={`px-2 py-1 font-mono font-bold rounded text-xs border ${
                                  isQualifying
                                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                      : 'bg-slate-900 text-slate-400 border-slate-800'
                              }`}>
                                {st.pts}
                              </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Qualifiers banner explanation */}
                                <div className="mt-5 p-3.5 bg-[#0f172a] rounded-lg border border-slate-800 flex items-start gap-2.5 text-xs text-slate-400 leading-relaxed font-sans">
                                    <Info size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                                    <div>
                    <span className="text-slate-200 font-bold">
                      {lang === 'es' ? 'Sistema de Progresión Estándar:' : (lang === 'bs' || lang === 'sr' || lang === 'hr') ? 'Standardni sistem napretka:' : 'Standard Progression System:'}
                    </span>{' '}
                                        {lang === 'es'
                                            ? 'Los dos mejores países de cada grupo (destacados arriba) avanzan a la fase de eliminación.'
                                            : (lang === 'bs' || lang === 'sr' || lang === 'hr')
                                                ? 'Dva najbolje plasirana tima iz svake struje/grupe (istaknuta iznad) prolaze u eliminacionu fazu.'
                                                : 'The top two ranked countries in each group (highlighted above) progress forward to the knockouts.'}
                                    </div>
                                </div>
                            </div>

                            {/* Match Score Editors: 5 Columns wide */}
                            <div className="lg:col-span-5 flex flex-col gap-4">

                                {/* Visualizer header */}
                                <div className="bg-[#131d31] border border-slate-800 rounded-xl p-5 shadow-lg">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 border-b border-slate-800/60 pb-4">
                                        <h3 className="text-sm font-bold font-mono tracking-widest text-slate-400 uppercase flex items-center gap-2">
                                            <Calendar size={15} className="text-blue-500" /> {t('fixturesTitle')}
                                        </h3>
                                        {isSuperUser && (
                                            <button
                                                onClick={saveChangesToDatabase}
                                                disabled={isSaving || !hasChanges}
                                                className={`text-xs font-bold px-3.5 py-2 rounded-xl flex items-center gap-1.5 transition-all outline-none border ${
                                                    hasChanges
                                                        ? 'bg-emerald-550 text-slate-900 border-emerald-400 hover:bg-emerald-400 hover:text-black shadow-[0_0_12px_rgba(16,185,129,0.2)] cursor-pointer font-black'
                                                        : 'bg-slate-900/60 text-slate-500 border-slate-800/80 cursor-not-allowed'
                                                }`}
                                            >
                                                <Save size={14} className={isSaving ? 'animate-spin' : ''} />
                                                {isSaving ? t('savingScores') : hasChanges ? t('saveDraftScores') : (lang === 'es' ? 'Sincronizado' : (lang === 'bs' || lang === 'sr' || lang === 'hr') ? 'Sinhronizovano' : 'Synced')}
                                            </button>
                                        )}
                                    </div>

                                    {!isSuperUser && (
                                        <div className="mb-4 bg-slate-950/40 text-slate-400 border border-slate-800 rounded-lg p-2.5 text-[11px] leading-snug">
                                            🔒 {t('onlyAdminsPredict')}
                                        </div>
                                    )}

                                    {isSuperUser && (
                                        <div className="mb-4">
                                            {saveStatus === 'success' && (
                                                <div className="bg-emerald-500/10 border border-emerald-550/30 text-emerald-400 text-xs px-3 py-2 rounded-lg font-mono flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-ping" />
                                                    {lang === 'es' ? '¡Resultados guardados con éxito!' : (lang === 'bs' || lang === 'sr' || lang === 'hr') ? '🎉 Rezultati uspešno sačuvani u bazu podataka!' : '🎉 Matches saved successfully to database!'}
                                                </div>
                                            )}
                                            {saveStatus === 'error' && (
                                                <div className="bg-red-500/10 border border-red-550/30 text-red-400 text-xs px-3 py-2 rounded-lg font-mono">
                                                    ❌ {lang === 'es' ? 'Error al guardar.' : (lang === 'bs' || lang === 'sr' || lang === 'hr') ? 'Greška pri čuvanju.' : 'Failed to write updates. Check Firebase settings.'}
                                                </div>
                                            )}
                                            {hasChanges && (
                                                <div className="bg-amber-500/10 border border-amber-500/20 text-amber-300 text-[11px] px-3 py-2 rounded-lg leading-snug">
                                                    ⚠️ {lang === 'es' ? 'Tienes cambios sin guardar. Haz clic en "Guardar puntuaciones" arriba.' : (lang === 'bs' || lang === 'sr' || lang === 'hr') ? '⚠️ Imate nesačuvane izmene. Kliknite na "Sačuvaj izmene" iznad da se obavi sinhronizacija.' : 'You have unsaved score changes. Click "Save Changes to Database" above to synchronize.'}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Fixtures organizer by round */}
                                    {[1, 2, 3].map(roundNum => {
                                        const roundMatches = activeGroupMatches.filter(m => m.round === roundNum);

                                        return (
                                            <div key={roundNum} className="mb-5 last:mb-0">
                                                {/* Round separator */}
                                                <div className="flex items-center justify-between mb-3 text-[10px] font-mono font-bold text-slate-500 tracking-wider">
                                                    <span>ROUND {roundNum}</span>
                                                    <span className="h-px bg-slate-805 flex-grow mx-3 opacity-20"></span>
                                                </div>

                                                <div className="flex flex-col gap-3">
                                                    {roundMatches.map(m => {
                                                        const matchKey = `${activeGroupId}_${m.home}_vs_${m.away}`;
                                                        const entry = (isSuperUser ? draftScores[matchKey] : scores[matchKey]) || { scoreA: "", scoreB: "" };

                                                        const teamHome = TEAMS.find(x => x.id === m.home)!;
                                                        const teamAway = TEAMS.find(x => x.id === m.away)!;
                                                        const isCompleted = entry.scoreA !== "" && entry.scoreB !== "";

                                                        return (
                                                            <div
                                                                key={m.id}
                                                                className={`p-3 bg-[#0f172a] rounded-lg border transition-all ${
                                                                    isCompleted ? 'border-slate-800/80 bg-[#0f172a]/80' : 'border-slate-800/40 hover:border-slate-705'
                                                                }`}
                                                            >
                                                                <div className="flex items-center justify-between gap-3">
                                                                    {/* Home Team */}
                                                                    <div className="flex items-center gap-2 w-[40%] min-w-0">
                                                                        <img
                                                                            src={`https://flagcdn.com/w20/${teamHome.flag}.png`}
                                                                            alt=""
                                                                            className="w-4.5 h-3 object-cover rounded-[1px] border border-slate-900 shadow flex-shrink-0"
                                                                            referrerPolicy="no-referrer"
                                                                            crossOrigin="anonymous"
                                                                        />
                                                                        <span className="text-xs font-semibold text-slate-200 truncate font-sans">{teamHome.name}</span>
                                                                    </div>

                                                                    {/* Interactive Score Panel */}
                                                                    <div className="flex items-center justify-center gap-1.5 w-[45%] flex-shrink-0">
                                                                        {isSuperUser ? (
                                                                            <>
                                                                                {/* Score Home control */}
                                                                                <div className="flex items-center gap-1">
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => decrementScore(matchKey, 'scoreA')}
                                                                                        className="w-5 h-5 bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white rounded flex items-center justify-center font-bold text-xs select-none cursor-pointer border border-slate-700/60 font-mono"
                                                                                    >
                                                                                        -
                                                                                    </button>

                                                                                    <input
                                                                                        type="text"
                                                                                        maxLength={2}
                                                                                        value={entry.scoreA}
                                                                                        onChange={(e) => handleScoreChange(matchKey, 'scoreA', e.target.value)}
                                                                                        className="w-7 h-7 bg-slate-950/80 border border-slate-800 focus:border-blue-500 rounded text-center text-xs font-bold font-mono text-white select-all focus:outline-none"
                                                                                        placeholder="-"
                                                                                        id={`${matchKey}-scoreA`}
                                                                                    />

                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => incrementScore(matchKey, 'scoreA')}
                                                                                        className="w-5 h-5 bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white rounded flex items-center justify-center font-bold text-xs select-none cursor-pointer border border-slate-700/60 font-mono"
                                                                                    >
                                                                                        +
                                                                                    </button>
                                                                                </div>

                                                                                {/* Separator / clear */}
                                                                                <div className="text-slate-600 font-mono text-[9px] px-0.5 select-none font-bold">vs</div>

                                                                                {/* Score Away control */}
                                                                                <div className="flex items-center gap-1">
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => decrementScore(matchKey, 'scoreB')}
                                                                                        className="w-5 h-5 bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white rounded flex items-center justify-center font-bold text-xs select-none cursor-pointer border border-slate-700/60 font-mono"
                                                                                    >
                                                                                        -
                                                                                    </button>

                                                                                    <input
                                                                                        type="text"
                                                                                        maxLength={2}
                                                                                        value={entry.scoreB}
                                                                                        onChange={(e) => handleScoreChange(matchKey, 'scoreB', e.target.value)}
                                                                                        className="w-7 h-7 bg-[#090d16] border border-slate-800 focus:border-blue-500 rounded text-center text-xs font-bold font-mono text-white select-all focus:outline-none"
                                                                                        placeholder="-"
                                                                                        id={`${matchKey}-scoreB`}
                                                                                    />

                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => incrementScore(matchKey, 'scoreB')}
                                                                                        className="w-5 h-5 bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white rounded flex items-center justify-center font-bold text-xs select-none cursor-pointer border border-slate-700/60 font-mono"
                                                                                    >
                                                                                        +
                                                                                    </button>
                                                                                </div>
                                                                            </>
                                                                        ) : (
                                                                            <div className="flex items-center gap-2 px-3 py-1 bg-[#1e293b]/60 border border-slate-800 rounded-lg text-xs font-bold font-mono select-none">
                                        <span className={entry.scoreA !== "" ? "text-white" : "text-slate-500"}>
                                          {entry.scoreA !== "" ? entry.scoreA : "-"}
                                        </span>
                                                                                <span className="text-slate-600 font-normal">:</span>
                                                                                <span className={entry.scoreB !== "" ? "text-white" : "text-slate-500"}>
                                          {entry.scoreB !== "" ? entry.scoreB : "-"}
                                        </span>
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    {/* Away Team */}
                                                                    <div className="flex items-center justify-end gap-2 w-[40%] min-w-0 text-right">
                                                                        <span className="text-xs font-semibold text-slate-200 truncate font-sans">{teamAway.name}</span>
                                                                        <img
                                                                            src={`https://flagcdn.com/w20/${teamAway.flag}.png`}
                                                                            alt=""
                                                                            className="w-4.5 h-3 object-cover rounded-[1px] border border-slate-900 shadow flex-shrink-0"
                                                                            referrerPolicy="no-referrer"
                                                                            crossOrigin="anonymous"
                                                                        />
                                                                    </div>
                                                                </div>

                                                                {/* Eraser button */}
                                                                {isCompleted && isSuperUser && (
                                                                    <div className="mt-2 flex justify-end">
                                                                        <button
                                                                            onClick={() => clearMatch(matchKey)}
                                                                            className="text-[9px] font-mono text-slate-500 hover:text-red-400 uppercase transition-all flex items-center gap-0.5"
                                                                            title="Reset score"
                                                                        >
                                                                            Clear Score
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                            </div>

                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
